import React from 'react'
import { useSelector } from 'react-redux'

import { AppState } from '@news-mono/web-common'
import {
    StyledActionLinks,
    StyledActionButton,
    subscribeButtonClassname,
    StyledPremiumIcon,
    StyledSkeleton,
} from './TheWestHeaderLogin.styled'
import { RegionalActionLink } from './RegionalActionLink'
import { UserNameLink, UserName } from './UserNameLink'
import { LogoutActionLink } from './LogoutActionLink'
import { useTheme } from '@emotion/react'
import { createSubscribeLinkClickEvent } from '../../user-registration/subscribe/createSubscribeClickLinkHandler'
import { THEWEST_ALL } from '../../user-registration/entitlements/common'

export interface AuthSiteLoginActionLinksProps {
    onSubscribeClick: () => void
    onLogoutClick: () => void
    onLoginClick: () => void
    onEvent: (event: any) => void
}

export const AuthSiteLoginActionLinks: React.FC<AuthSiteLoginActionLinksProps> = ({
    onSubscribeClick,
    onLogoutClick,
    onLoginClick,
    onEvent,
}) => {
    const {
        userName,
        isLoggedIn,
        hasAttemptedValidation,
        entitlements,
        subscriptionType,
    } = useSelector((state: AppState) => state.authentication)
    const { section } = useTheme()
    const isRegionalSite = section !== 'default'

    /**
     * Check to see if the user likely has an account on SSW before we link there,
     * Regwall users only have `thewest:registered`
     */
    const showMyAccountLink =
        isLoggedIn &&
        entitlements &&
        entitlements.includes(THEWEST_ALL) &&
        subscriptionType !== 'corporate-site'

    return isLoggedIn ? (
        <ActionLinksAuthenticated
            isRegionalSite={isRegionalSite}
            onLogoutClick={onLogoutClick}
            userName={userName}
            hasAccount={showMyAccountLink}
        />
    ) : (
        <ActionLinksUnauthenticated
            isRegionalSite={isRegionalSite}
            onSubscribeClick={onSubscribeClick}
            onLoginClick={onLoginClick}
            hasAttemptedValidation={hasAttemptedValidation}
            onEvent={onEvent}
        />
    )
}

export interface ActionLinksUnauthenticatedProps {
    isRegionalSite: boolean
    onLoginClick: () => void
    onSubscribeClick: () => void
    hasAttemptedValidation: boolean
    onEvent: (event: any) => void
}

export const ActionLinksUnauthenticated: React.FC<ActionLinksUnauthenticatedProps> = ({
    isRegionalSite,
    hasAttemptedValidation,
    onLoginClick,
    onSubscribeClick,
    onEvent,
}) => (
    <StyledActionLinks>
        {isRegionalSite && <RegionalActionLink />}

        <StyledActionButton isPrimary={false} onClick={onLoginClick}>
            {hasAttemptedValidation ? 'Login' : <StyledSkeleton count={1} />}
        </StyledActionButton>

        <StyledActionButton
            className={subscribeButtonClassname}
            onClick={() => {
                createSubscribeLinkClickEvent('header', onEvent)()
                onSubscribeClick()
            }}
            isPrimary={true}
        >
            {hasAttemptedValidation ? (
                <React.Fragment>
                    <StyledPremiumIcon />
                    Subscribe
                </React.Fragment>
            ) : (
                <StyledSkeleton count={1} />
            )}
        </StyledActionButton>
    </StyledActionLinks>
)

export interface ActionLinksAuthenticatedProps {
    isRegionalSite: boolean
    userName: string
    hasAccount: boolean
    onLogoutClick: () => void
}

export const ActionLinksAuthenticated: React.FC<ActionLinksAuthenticatedProps> = ({
    isRegionalSite,
    userName,
    hasAccount,
    onLogoutClick,
}) => (
    <StyledActionLinks>
        {isRegionalSite && <RegionalActionLink />}
        {hasAccount ? (
            <UserNameLink userName={userName} />
        ) : (
            <UserName userName={userName} />
        )}
        <LogoutActionLink onLogoutClick={onLogoutClick} />
    </StyledActionLinks>
)
